import { Component, Input, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { DecimalPipe } from '@angular/common';
import { FeedbackView, SecretaryJudgeValues } from '../feedback.service';
import { categoryResultsUrl, eventPageUrl } from '../feedback-links';
import { CriteriaLabelsService } from '../criteria-labels.service';
import { SportService } from 'app/services/sport/sport.service';
import { pointTypeOfJudge, valueKeys } from 'app/services/sport/judging-rules';

type Criterion = {
  label: string;
  value: number;
};

type JudgeCard = {
  judgeCode: string;
  criteria: Criterion[];
  total: number;
  feedback: string | null;
  collapsed: boolean;
};

type Program = {
  judges: JudgeCard[];
  collapsed: boolean;
};

type Round = {
  roundName: string;
  programs: Program[];
};

@Component({
  selector: 'gfl-feedback-view',
  standalone: true,
  imports: [TranslateModule, DecimalPipe],
  templateUrl: './gfl-feedback-view.component.html',
  styleUrls: ['./gfl-feedback-view.component.scss'],
})
export class GflFeedbackViewComponent implements OnInit {
  @Input() view: FeedbackView;

  rounds: Round[] = [];

  constructor(
    private criteriaLabels: CriteriaLabelsService,
    private sportService: SportService
  ) {}

  ngOnInit() {
    this.applyData(this.view);
  }

  get eventUrl(): string {
    return eventPageUrl(this.view.eventId);
  }

  get resultsUrl(): string {
    return categoryResultsUrl(this.view.eventId, this.view.categoryId);
  }

  get totalPrograms(): number {
    return this.rounds.reduce((sum, round) => sum + round.programs.length, 0);
  }

  toggleProgram(program: Program) {
    program.collapsed = !program.collapsed;
  }

  toggleJudge(judge: JudgeCard) {
    judge.collapsed = !judge.collapsed;
  }

  anyExpanded(): boolean {
    return this.rounds.some((round) =>
      round.programs.some((program) => !program.collapsed || program.judges.some((judge) => !judge.collapsed))
    );
  }

  toggleAll() {
    const collapse = this.anyExpanded();
    this.rounds.forEach((round) =>
      round.programs.forEach((program) => {
        program.collapsed = collapse;
        program.judges.forEach((judge) => (judge.collapsed = collapse));
      })
    );
  }

  private applyData(view: FeedbackView) {
    this.rounds = view.rounds.map((round) => ({
      roundName: round.roundName,
      programs: round.programs.map((program) => ({
        collapsed: false,
        judges: [...program.feedbacks]
          .sort((a, b) => this.compareJudgeCode(a.judgeCode, b.judgeCode))
          .map((feedback) => {
            const criteria = this.criteria(feedback.judgeValues, view.judgingRulesProfileId, feedback.judgeCode);
            return {
              judgeCode: feedback.judgeCode,
              criteria,
              total: criteria.reduce((sum, criterion) => sum + criterion.value, 0),
              feedback: feedback.feedback,
              collapsed: false,
            };
          }),
      })),
    }));
  }

  /**
   * The criteria in the order the judging rules profile declares them. Each value key gets its label before the keys
   * the judge has no value for are left out, so that every label stays with its own value.
   */
  private criteria(
    judgeValues: SecretaryJudgeValues | null,
    profileId: string | undefined,
    judgeCode: string
  ): Criterion[] {
    if (!judgeValues || !profileId) {
      return [];
    }
    const pointType = pointTypeOfJudge(
      this.sportService.getJudgingRulesProfileAttr(profileId, 'judgeGroups'),
      judgeCode
    );
    const keys = valueKeys(this.sportService.getJudgingRulesProfileAttr(profileId, 'pointTypes'), pointType);
    const labels = this.criteriaLabels.labels('gfl', profileId, judgeCode, keys.length);
    return keys
      .map((key, index) => ({ label: labels[index], value: judgeValues[key] }))
      .filter((criterion) => criterion.value !== undefined && criterion.value !== null);
  }

  private compareJudgeCode(a: string, b: string): number {
    const prefixA = a.replace(/\d+$/, '');
    const prefixB = b.replace(/\d+$/, '');
    if (prefixA !== prefixB) {
      return prefixA.localeCompare(prefixB);
    }
    return this.judgeNumber(a) - this.judgeNumber(b);
  }

  private judgeNumber(code: string): number {
    const match = code.match(/\d+$/);
    return match ? Number(match[0]) : 0;
  }
}
